import React, { useEffect, useMemo, useState } from 'react';
import {
	AppBar,
	Toolbar,
	Typography,
	Avatar,
	Box,
	Grid,
	Paper,
	Chip,
	Button,
	Stack,
	Divider,
	Alert,
	List,
	ListItem,
	ListItemText,
	ListItemIcon,
	IconButton,
	Tooltip,
	CircularProgress,
	Collapse,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	FormControl,
	InputLabel,
	Select,
	MenuItem
} from '@mui/material';
import { toast } from 'react-toastify';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import WindowRoundedIcon from '@mui/icons-material/WindowRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import PhoneForwardedRoundedIcon from '@mui/icons-material/PhoneForwardedRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import SkipNextRoundedIcon from '@mui/icons-material/SkipNextRounded';
import CallSplitRoundedIcon from '@mui/icons-material/CallSplitRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import FingerprintRoundedIcon from '@mui/icons-material/FingerprintRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import FiberManualRecordRoundedIcon from '@mui/icons-material/FiberManualRecordRounded';
import { styled, alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { LoadingContainer } from '../../../common/helpers/LoadingContainer';
import AuthService from '../../../services/authService';
import LanguageSwitcher from '../../../common/LanguageSwitcher/LanguageSwitcher';
import './managerCabinet.scss';
import { useManagerCabinet, WS_STATUS } from './useManagerCabinet';

// Антиспам: после успешного вызова следующего талона кнопка блокируется на 60 сек,
// чтобы менеджер случайно не «прощёлкал» несколько номеров подряд.
const CALL_COOLDOWN_SEC = 60;
const CALL_COOLDOWN_KEY = 'mc_callNextCooldownUntil';

function formatPrettyKZ(phone) {
	const d = String(phone || '').replace(/\D/g, '');
	if (!d) return '';
	const last10 = d.slice(-10);
	if (last10.length !== 10) return phone;
	return `+7 ${last10.slice(0, 3)} ${last10.slice(3, 6)}-${last10.slice(6, 8)}-${last10.slice(8, 10)}`;
}

const StatusChip = ({ status }) => {
	const { t } = useTranslation();
	const map = {
		CALLED:      { color: 'warning', icon: <CampaignRoundedIcon fontSize='small' /> },
		DONE:        { color: 'success', icon: <CheckCircleRoundedIcon fontSize='small' /> },
		IN_PROGRESS: { color: 'info' },
		WAITING:     { color: 'default' }
	};
	const cfg = map[status] || { color: 'default' };
	const label = t(`manager.status.${status}`, { defaultValue: status });
	return <Chip size='small' color={cfg.color} icon={cfg.icon} label={label} />;
};

const BigCard = styled(Paper)(({ theme }) => ({
	position: 'relative',
	overflow: 'hidden',
	borderRadius: 20,
	padding: theme.spacing(3),
	height: '100%',
	background:
		theme.palette.mode === 'dark'
			? alpha(theme.palette.primary.dark, 0.25)
			: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.25)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 60%)`,
	boxShadow: theme.shadows[6],
	[theme.breakpoints.down('sm')]: {
		borderRadius: 16,
		padding: theme.spacing(2)
	}
}));

const WindowBadge = styled('div')(({ theme }) => ({
	display: 'inline-flex',
	alignItems: 'center',
	gap: 12,
	padding: '12px 18px',
	borderRadius: 16,
	background: alpha(theme.palette.primary.main, 0.1),
	border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`
}));

const CallButton = styled(Button)(({ theme }) => ({
	textTransform: 'none',
	fontWeight: 800,
	fontSize: 18,
	borderRadius: 16,
	[theme.breakpoints.down('sm')]: {
		width: '100%',
		height: 64,
		fontSize: 20,
		borderRadius: 14
	},
	[theme.breakpoints.up('sm')]: {
		height: 56,
		paddingLeft: 24,
		paddingRight: 24
	}
}));

function ManagerCabinet() {
	const { t } = useTranslation();
	AuthService.checkPermissionToPage(['MANAGER']);

	const { tickets, userInfo, loading, errorMessage, infoMessage, serviceTypes, waitingCount, wsStatus, callNextTicket, completeTicket, repeatCall, skipTicket, redirectTicket } = useManagerCabinet();
	const [calling, setCalling] = useState(false);
	const [cooldown, setCooldown] = useState(0); // секунд до разблокировки «Вызвать следующий»
	const [historyOpen, setHistoryOpen] = useState(false);
	const [redirectOpen, setRedirectOpen] = useState(false);
	const [redirectTarget, setRedirectTarget] = useState('');
	const [redirecting, setRedirecting] = useState(false);

	const { current, previous } = useMemo(() => {
		const list = tickets || [];
		const current =
			list.find(t => ['CALLED', 'IN_PROGRESS', 'WAITING'].includes(t.ticketStatus)) ||
			list[0] ||
			null;
		const previous = list.filter(t => t !== current).slice(0, 4);
		return { current, previous };
	}, [tickets]);

	useEffect(() => {
		const onKey = e => {
			if ((e.key === 'n' || e.key === 'N') && !calling && !loading && cooldown <= 0) {
				e.preventDefault();
				handleCallNext();
			}
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [calling, loading, cooldown]); // eslint-disable-line react-hooks/exhaustive-deps

	// Восстановить остаток кулдауна после перезагрузки страницы
	useEffect(() => {
		const until = Number(localStorage.getItem(CALL_COOLDOWN_KEY) || 0);
		const remaining = Math.ceil((until - Date.now()) / 1000);
		if (remaining > 0) setCooldown(remaining);
	}, []);

	// Тикаем раз в секунду, пересчитывая остаток от абсолютного времени (устойчиво к троттлингу вкладки)
	useEffect(() => {
		if (cooldown <= 0) return undefined;
		const id = setInterval(() => {
			const until = Number(localStorage.getItem(CALL_COOLDOWN_KEY) || 0);
			const remaining = Math.max(0, Math.ceil((until - Date.now()) / 1000));
			setCooldown(remaining);
			if (remaining <= 0) localStorage.removeItem(CALL_COOLDOWN_KEY);
		}, 1000);
		return () => clearInterval(id);
	}, [cooldown > 0]); // eslint-disable-line react-hooks/exhaustive-deps

	async function handleCallNext() {
		if (calling || cooldown > 0) return;
		setCalling(true);
		try {
			const res = await callNextTicket();
			// Блокируем кнопку только если реально вызвали талон
			if (res?.ok) {
				const until = Date.now() + CALL_COOLDOWN_SEC * 1000;
				localStorage.setItem(CALL_COOLDOWN_KEY, String(until));
				setCooldown(CALL_COOLDOWN_SEC);
			}
		} finally {
			setCalling(false);
		}
	}

	async function handleComplete() {
		setCalling(true);
		try { await completeTicket(); } finally { setCalling(false); }
	}

	async function handleRepeat() {
		await repeatCall();
	}

	async function handleSkip() {
		setCalling(true);
		try { await skipTicket(); } finally { setCalling(false); }
	}

	const redirectOptions = (serviceTypes || []).filter(st => st.id !== current?.serviceType?.id);

	function openRedirect() {
		setRedirectTarget('');
		setRedirectOpen(true);
	}

	async function handleRedirectConfirm() {
		if (!redirectTarget) return;
		setRedirecting(true);
		try {
			const res = await redirectTicket(redirectTarget);
			if (res.ok) {
				toast.success(t('manager.redirectSuccess', { ticket: res.ticket?.ticketNumber || '' }));
				setRedirectOpen(false);
			} else {
				toast.error(t('manager.redirectError'));
			}
		} finally {
			setRedirecting(false);
		}
	}

	if (loading) return <LoadingContainer />;

	return (
		<Box className='manager-cabinet'>
			{/* ── AppBar ─────────────────────────────────────────────── */}
			<AppBar position='sticky' elevation={0} className='mc-appbar'>
				<Toolbar className='mc-toolbar'>
					<Stack direction='row' spacing={1.5} alignItems='center'>
						<Avatar sx={{ width: 36, height: 36, fontSize: 16 }}>
							{(userInfo?.name || 'M')[0]}
						</Avatar>
						<Box>
							<Typography variant='subtitle1' sx={{ fontWeight: 700, lineHeight: 1, fontSize: { xs: 14, sm: 16 } }}>
								{userInfo?.surname} {userInfo?.name}
							</Typography>
							<Typography variant='caption' color='inherit' sx={{ opacity: 0.75, fontSize: 11 }}>
								{t('manager.role')}
							</Typography>
						</Box>
					</Stack>

					{AuthService.isLoggedIn() && (
						<Stack direction='row' spacing={0.5} alignItems='center'>
							<LanguageSwitcher variant='dark' />

							{/* Полные кнопки на desktop */}
							<Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1 }}>
								<Button
									component={RouterLink}
									to='/'
									variant='outlined'
									color='inherit'
									startIcon={<HomeRoundedIcon />}
									sx={{
										textTransform: 'none',
										fontWeight: 700,
										borderRadius: 2,
										height: 36,
										px: 1.5,
										borderColor: 'rgba(255,255,255,0.55)',
										'&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' }
									}}
								>
									{t('common.home')}
								</Button>
								<Button
									onClick={AuthService.logout}
									variant='outlined'
									color='inherit'
									startIcon={<LogoutRoundedIcon />}
									sx={{
										textTransform: 'none',
										fontWeight: 700,
										borderRadius: 2,
										height: 36,
										px: 1.5,
										borderColor: 'rgba(255,255,255,0.55)',
										'&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' }
									}}
								>
									{t('common.logout')}
								</Button>
							</Box>

							{/* Иконки на мобиле */}
							<Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 0.5 }}>
								<IconButton
									component={RouterLink}
									to='/'
									color='inherit'
									sx={{ border: '1px solid rgba(255,255,255,0.4)', borderRadius: 2, p: 0.75 }}
								>
									<HomeRoundedIcon />
								</IconButton>
								<IconButton
									onClick={AuthService.logout}
									color='inherit'
									sx={{ border: '1px solid rgba(255,255,255,0.4)', borderRadius: 2, p: 0.75 }}
								>
									<LogoutRoundedIcon />
								</IconButton>
							</Box>
						</Stack>
					)}
				</Toolbar>
			</AppBar>

			{/* ── Контент ────────────────────────────────────────────── */}
			<Box className='mc-content'>
				{errorMessage && <Alert severity='error' sx={{ mb: 2 }}>{errorMessage}</Alert>}
				{infoMessage && <Alert severity='info' sx={{ mb: 2 }}>{infoMessage}</Alert>}

				<Grid container spacing={2.5}>
					{/* Текущий талон + кнопки */}
					<Grid item xs={12} md={8} order={{ xs: 1, md: 2 }}>
						<BigCard>
							{/* Департамент + номер окна */}
							<Stack
								direction='row'
								alignItems='center'
								justifyContent='space-between'
								sx={{ mb: 2 }}
							>
								<Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
									{current?.serviceTypes?.length > 0
										? current.serviceTypes.map(st => (
											<Chip key={st.id} size='small' label={st.nameRu}
												sx={{ backgroundColor: st.color || '#EDE9FE', fontWeight: 700, fontSize: 12 }} />
										))
										: <Typography variant='subtitle1' sx={{ fontWeight: 700, fontSize: { xs: 13, sm: 15 } }}>—</Typography>
									}
								</Stack>
								{current?.windowNumber && (
									<WindowBadge>
										<WindowRoundedIcon />
										<Typography variant='h2' sx={{ fontWeight: 900, lineHeight: 1, letterSpacing: 0.5, fontSize: { xs: 36, sm: 48 } }}>
											{current.windowNumber}
										</Typography>
									</WindowBadge>
								)}
							</Stack>
							<Divider sx={{ mb: 2 }} />

							{/* Текущий талон */}
							<Box className='mc-current' sx={{ mb: 2.5 }}>
								{current ? (
									<>
										<Typography variant='overline' sx={{ opacity: 0.7 }}>{t('manager.currentClient')}</Typography>
										<Typography className='mc-ticket-number'>{current.ticketNumber}</Typography>
										<Stack spacing={1} sx={{ mt: 1.5 }}>
											<StatusChip status={current.ticketStatus} />
											{current.fullName && (
												<Stack direction='row' spacing={0.75} alignItems='center'>
													<PersonRoundedIcon fontSize='small' sx={{ opacity: 0.6 }} />
													<Typography variant='body2'>{current.fullName}</Typography>
												</Stack>
											)}
											{current.iin && (
												<Stack direction='row' spacing={0.75} alignItems='center'>
													<FingerprintRoundedIcon fontSize='small' sx={{ opacity: 0.6 }} />
													<Typography variant='body2' color='text.secondary'>{current.iin}</Typography>
												</Stack>
											)}
											{current.phoneNumber && (
												<Stack direction='row' spacing={0.75} alignItems='center'>
													<PhoneIphoneIcon fontSize='small' />
													<Typography variant='body2' color='text.secondary'>{formatPrettyKZ(current.phoneNumber)}</Typography>
												</Stack>
											)}
											{current.program && (
												<Stack direction='row' spacing={0.75} alignItems='center'>
													<SchoolRoundedIcon fontSize='small' sx={{ opacity: 0.6 }} />
													<Typography variant='body2' color='text.secondary'>{current.program}</Typography>
												</Stack>
											)}
										</Stack>
									</>
								) : (
									<Typography variant='body2' color='text.secondary' sx={{ py: 1 }}>{t('manager.noActiveTicket')}</Typography>
								)}
							</Box>

							{/* Кнопки действий */}
							<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 2 }}>
								<Tooltip title={t('manager.hotkey')} disableTouchListener>
									<span>
										<Button
											variant='contained'
											size='large'
											onClick={handleCallNext}
											disabled={calling || cooldown > 0}
											startIcon={calling || cooldown > 0 ? null : <CampaignRoundedIcon />}
											fullWidth
											sx={{ height: 56, textTransform: 'none', fontWeight: 700, fontSize: 15, borderRadius: 3, bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}
										>
											{calling
												? <CircularProgress size={20} sx={{ color: 'inherit' }} />
												: cooldown > 0
													? t('manager.callNextWait', { sec: cooldown })
													: t('manager.callNext')}
										</Button>
									</span>
								</Tooltip>

								<Button
									variant='contained'
									size='large'
									onClick={handleRepeat}
									disabled={!current || calling}
									startIcon={<PhoneForwardedRoundedIcon />}
									fullWidth
									sx={{ height: 56, textTransform: 'none', fontWeight: 700, fontSize: 15, borderRadius: 3, bgcolor: '#f97316', '&:hover': { bgcolor: '#ea580c' } }}
								>
									{t('manager.repeatCall')}
								</Button>

								<Button
									variant='contained'
									size='large'
									onClick={handleComplete}
									disabled={!current || calling}
									startIcon={<CheckCircleOutlineRoundedIcon />}
									fullWidth
									sx={{ height: 56, textTransform: 'none', fontWeight: 700, fontSize: 15, borderRadius: 3, bgcolor: '#1e3a8a', '&:hover': { bgcolor: '#1e40af' } }}
								>
									{t('manager.complete')}
								</Button>

								<Button
									variant='outlined'
									size='large'
									onClick={handleSkip}
									disabled={!current || calling}
									startIcon={<SkipNextRoundedIcon />}
									fullWidth
									sx={{ height: 56, textTransform: 'none', fontWeight: 700, fontSize: 15, borderRadius: 3 }}
								>
									{t('manager.skip')}
								</Button>

								<Button
									variant='outlined'
									color='secondary'
									size='large'
									onClick={openRedirect}
									disabled={!current || calling}
									startIcon={<CallSplitRoundedIcon />}
									fullWidth
									sx={{ height: 56, textTransform: 'none', fontWeight: 700, fontSize: 15, borderRadius: 3, gridColumn: '1 / -1' }}
								>
									{t('manager.redirect')}
								</Button>
							</Box>
						</BigCard>
					</Grid>

					{/* Очередь + История */}
					<Grid item xs={12} md={4} order={{ xs: 2, md: 1 }}>
						{/* Счётчик ожидающих в очереди (реальное время) */}
						<Paper
							elevation={0}
							sx={{
								p: 2.5,
								mb: 2.5,
								borderRadius: 3,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								color: '#fff',
								background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)'
							}}
						>
							<Stack direction='row' spacing={1.5} alignItems='center'>
								<GroupsRoundedIcon sx={{ fontSize: 40, opacity: 0.9 }} />
								<Box>
									<Typography variant='caption' sx={{ opacity: 0.85, fontWeight: 600, letterSpacing: 0.3 }}>
										{t('manager.queueWaiting')}
									</Typography>
									<Typography sx={{ fontWeight: 900, fontSize: 42, lineHeight: 1 }}>
										{waitingCount}
									</Typography>
								</Box>
							</Stack>
							<Tooltip title={wsStatus === WS_STATUS.CONNECTED ? t('manager.live') : t('manager.offline')}>
								<Stack direction='row' spacing={0.5} alignItems='center' sx={{ opacity: 0.9 }}>
									<FiberManualRecordRoundedIcon
										sx={{
											fontSize: 12,
											color: wsStatus === WS_STATUS.CONNECTED ? '#22c55e' : '#94a3b8'
										}}
									/>
									<Typography variant='caption' sx={{ fontWeight: 700 }}>
										{wsStatus === WS_STATUS.CONNECTED ? t('manager.live') : t('manager.offline')}
									</Typography>
								</Stack>
							</Tooltip>
						</Paper>

						<Paper className='mc-side' elevation={0}>
							<Stack
								direction='row'
								alignItems='center'
								justifyContent='space-between'
								onClick={() => setHistoryOpen(o => !o)}
								sx={{
									cursor: { xs: 'pointer', md: 'default' },
									userSelect: 'none',
									pb: { xs: historyOpen ? 0 : 0, md: 0 }
								}}
							>
								<Stack direction='row' spacing={1.2} alignItems='center'>
									<HistoryRoundedIcon fontSize='small' />
									<Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
										{t('manager.previousTickets')}
									</Typography>
								</Stack>
								<Stack direction='row' spacing={1} alignItems='center'>
									<Chip size='small' label={`${previous.length}/4`} />
									<Box sx={{ display: { xs: 'flex', md: 'none' }, color: 'text.secondary' }}>
										{historyOpen ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
									</Box>
								</Stack>
							</Stack>

							<Box sx={{ display: { xs: 'none', md: 'block' } }}>
								<Divider sx={{ my: 1.5 }} />
								<HistoryList previous={previous} />
							</Box>
							<Collapse in={historyOpen} sx={{ display: { xs: 'block', md: 'none' } }}>
								<Divider sx={{ my: 1.5 }} />
								<HistoryList previous={previous} />
							</Collapse>
						</Paper>
					</Grid>
				</Grid>
			</Box>

			{/* ── Диалог перенаправления ─────────────────────────────── */}
			<Dialog open={redirectOpen} onClose={() => !redirecting && setRedirectOpen(false)} maxWidth='xs' fullWidth>
				<DialogTitle sx={{ fontWeight: 700 }}>{t('manager.redirectTitle')}</DialogTitle>
				<DialogContent>
					<FormControl fullWidth size='small' sx={{ mt: 1 }}>
						<InputLabel>{t('manager.redirectSelect')}</InputLabel>
						<Select
							value={redirectTarget}
							label={t('manager.redirectSelect')}
							onChange={e => setRedirectTarget(e.target.value)}
						>
							{redirectOptions.map(st => (
								<MenuItem key={st.id} value={st.id}>{st.nameRu}</MenuItem>
							))}
						</Select>
					</FormControl>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
					<Button onClick={() => setRedirectOpen(false)} disabled={redirecting} sx={{ textTransform: 'none', borderRadius: 2 }}>
						{t('common.cancel')}
					</Button>
					<Button
						variant='contained'
						onClick={handleRedirectConfirm}
						disabled={redirecting || !redirectTarget}
						startIcon={redirecting ? null : <CallSplitRoundedIcon />}
						sx={{ textTransform: 'none', borderRadius: 2, minWidth: 120 }}
					>
						{redirecting ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : t('manager.redirectConfirm')}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}

function HistoryList({ previous }) {
	const { t } = useTranslation();

	if (previous.length === 0) {
		return (
			<Typography variant='body2' color='text.secondary'>
				{t('manager.noHistory')}
			</Typography>
		);
	}
	return (
		<List dense disablePadding>
			{previous.map(ticket => (
				<ListItem key={ticket.ticketNumber} className='mc-prev-item'>
					<ListItemIcon className='mc-prev-icon'>
						<CheckCircleRoundedIcon />
					</ListItemIcon>
					<ListItemText
						primary={
							<Box display='flex' alignItems='center' justifyContent='space-between'>
								<Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
									{ticket.ticketNumber}
								</Typography>
								<StatusChip status={ticket.ticketStatus} />
							</Box>
						}
						secondary={
							<Stack direction='row' spacing={0.75} alignItems='center' sx={{ mt: 0.25 }}>
								<PhoneIphoneIcon fontSize='inherit' />
								<Typography variant='caption' color='text.secondary'>
									{String(ticket.phoneNumber || '').replace(/\D/g, '').slice(-10).replace(/(\d{3})(\d{3})(\d{2})(\d{2})/, '+7 $1 $2-$3-$4')}
								</Typography>
							</Stack>
						}
					/>
				</ListItem>
			))}
		</List>
	);
}

export default ManagerCabinet;
